import { default as HTTP, requestCanceller } from '@/utils/http';
import localStore from 'store';
import router from '@/router';
import jwtDecode from 'jwt-decode';

const logout = async (preserveRouteWhenLoggingBackIn) => {
  try {
    localStore.remove('auth');

    await HTTP({
      method: 'post',
      url: 'logout',
      meta: {
        type: 'logout',
      },
    });
  } catch {
    // Route to login if we manage to expire the token or not
  }

  const options = { name: 'Login' };

  if (preserveRouteWhenLoggingBackIn) {
    options.params = { interceptedFrom: router.currentRoute };
  }

  await router.push(options);
};

const refreshToken = async (state, dispatch, commit) => {
  if (!state.refreshToken) {
    dispatch('LOGOUT');
  }

  const { data: { data: token } } = await HTTP({
    method: 'post',
    url: 'attempt-refresh',
    auth: false,
    meta: {
      type: 'attempt-refresh',
    },
    data: {
      refreshToken: state.refreshToken,
    },
  });

  commit('SET_TOKENS', token);

  await dispatch('REFRESH_INITIAL_DATA', undefined, { root: true });
};

const defaultState = () => ({
  accessToken: null,
  refreshToken: null,
  tokenType: null,
  expiresIn: null,
  currentLogoutRequest: null,
  currentRefreshRequest: null,
  expirationTimestamp: 0,
  isFormattedCorrectly: false,
  now: Math.round(Date.now() / 1000),
  previewKey: null,
});

const authModule = {
  namespaced: true,
  state: defaultState(),
  mutations: {
    RESET_STATE(state) {
      Object.assign(state, defaultState());
    },
    SET_TOKENS(state, token) {
      state.accessToken = token.access_token;
      state.refreshToken = token.refresh_token;
      state.tokenType = token.token_type;
      state.expiresIn = token.expires_in;

      localStore.set('auth', {
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        tokenType: state.tokenType,
        expiresIn: state.expiresIn,
      });

      try {
        const { exp: tokenExpirationTimestamp } = jwtDecode(state.accessToken);
        state.expirationTimestamp = tokenExpirationTimestamp - process.env.VUE_APP_TOKEN_EXPIRY_GAP;
        state.isFormattedCorrectly = true;
      } catch (e) {
        state.expirationTimestamp = 0;
        state.isFormattedCorrectly = false;
      }
    },
    UPDATE_TIME(state) {
      state.now = Math.round(Date.now() / 1000);
    },
    SET_PREVIEW_KEY(state, { previewKey }) {
      state.previewKey = previewKey;
    },
  },
  actions: {
    RESET_STATE({ commit }) {
      commit('RESET_STATE');
    },
    SET_ACCESS_TOKEN({ commit }, token) {
      commit('SET_TOKENS', token);
    },
    async LOGIN({ commit, dispatch }, { email, password }) {
      const { data: { data: tokenObject } } = await HTTP({
        method: 'post',
        url: 'login',
        auth: false,
        meta: {
          type: 'login',
        },
        data: {
          email,
          password,
        },
        cancelToken: requestCanceller.getToken(true), // reset cancel token
      });

      commit('SET_TOKENS', tokenObject);

      await dispatch('REFRESH_INITIAL_DATA', undefined, { root: true });
      return tokenObject;
    },
    async LOGOUT({ state, dispatch }, preserveRouteWhenLoggingBackIn = true) {
      // If order to prevent duplicate attempts, if there is no in progress attempt start one, otherwise wait for the
      // current attempt.
      if (!state.currentLogoutRequest) {
        state.currentLogoutRequest = logout(preserveRouteWhenLoggingBackIn)
          .finally(() => {
            dispatch('RESET_STATE', null, { root: true });
            state.currentLogoutRequest = null;
            state.accessToken = null;
          });
      }

      await state.currentLogoutRequest;
    },
    async ATTEMPT_REFRESH({ state, dispatch, commit }) {
      // If order to prevent duplicate attempts, if there is no in progress attempt start one, otherwise wait for the
      // current attempt.
      if (!state.currentRefreshRequest) {
        state.currentRefreshRequest = refreshToken(state, dispatch, commit)
          .finally(() => {
            state.currentRefreshRequest = null;
          });
      }

      await state.currentRefreshRequest;
    },
    // eslint-disable-next-line no-empty-pattern
    async VERIFY_EMAIL({}, { token }) {
      return HTTP({
        method: 'post',
        url: 'company/verify',
        auth: false,
        data: {
          token,
        },
        suppressServerErrors: true,
      });
    },
    GET_PREVIEW_KEY({ commit }) {
      return HTTP({
        method: 'get',
        url: 'users/current/preview-key',
        auth: true,
      }).then(({ data }) => {
        commit('SET_PREVIEW_KEY', { previewKey: data.data.preview_key });
      });
    },
  },
  getters: {
    isFormattedCorrectly: state => state.isFormattedCorrectly,
    isInDate: state => state.now < state.expirationTimestamp,
    isAuthenticated: (state, getters) => !!(getters.isFormattedCorrectly && getters.isInDate && getters.accessToken),
    accessToken: state => state.accessToken,
    previewKey: state => state.previewKey,
  },
};

export default authModule;
